// v11
// Path: frontend/src/components/DataCanvasV3/index.jsx
// Inputs: defaultViewId (optional), token
// Outputs: React Component wrapping Data Canvas workspace and collapsible Universe Explorer Sidebar
// Summary: Master container for V3 Data Canvas. Renders collapsible Universe Explorer Sidebar with contextual active grid focus, Structure Mode (Edit) vs Data Mode (View) toggle, pure div layout grids, CanvasFileItem widgets, and decoupled DataCanvasV3.css stylesheet.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import CanvasGrid from './CanvasGrid';
import CanvasFileItem from './CanvasFileItem';
import ConnectionManager from './ConnectionManager';
import UniverseExplorerSidebar from './UniverseExplorerSidebar';
import CanvasDispatcher from './CanvasDispatcher';
import './DataCanvasV3.css';

const DataCanvas = ({ defaultViewId, token }) => {
    const [viewId, setViewId] = useState(defaultViewId || null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // UI states
    const [showConnectionManager, setShowConnectionManager] = useState(false);
    const [showAllGridControls, setShowAllGridControls] = useState(true);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isStructureMode, setIsStructureMode] = useState(false);
    const [activeGrid, setActiveGrid] = useState(null);
    const [connectionsTree, setConnectionsTree] = useState([]);

    const [draftConfig, setDraftConfig] = useState(null);
    const [globalParameters, setGlobalParameters] = useState({});
    
    // View listing & creation state
    const [views, setViews] = useState([]);
    const [selectedTableFilter, setSelectedTableFilter] = useState('All');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newViewName, setNewViewName] = useState('');
    const [isSavingView, setIsSavingView] = useState(false);

    const saveTimerRef = useRef(null);
    const currentViewIdRef = useRef(viewId);
    useEffect(() => { currentViewIdRef.current = viewId; }, [viewId]);

    // ── File Template Picker State ─────────────────────────────────────────────
    // When a user drops a file and multiple templates are available, show a modal.
    const [pendingFileDrop, setPendingFileDrop] = useState(null); // { filePath, fileName, extension, fileSize, fileDateModified, connectionPrefix, templates }
    const [selectedPickerTemplateId, setSelectedPickerTemplateId] = useState(null);

    // Fetch 7-tier connections tree for Universe Explorer Sidebar
    useEffect(() => {
        const fetchTree = async () => {
            if (!token) return;
            try {
                const res = await fetch('/api/v3/connections', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setConnectionsTree(data);
                }
            } catch (err) {
                console.error("Failed to fetch connections tree for Universe Explorer:", err);
            }
        };
        fetchTree();
    }, [token]);

    const defaultConfig = useMemo(() => ({ levels: {}, polymorphicRules: {} }), []);
    const activeConfig = draftConfig || defaultConfig;

    // Dynamically derive top-level root grid instance IDs from levels (levels not listed as child subgrids)
    const rootGridKeys = useMemo(() => {
        const levels = activeConfig.levels || {};
        const childKeys = new Set();
        Object.values(levels).forEach(lvl => {
            (lvl.subgrids || []).forEach(sg => {
                const childId = typeof sg === 'string' ? sg : (sg.instanceId || sg.id);
                if (childId) childKeys.add(childId);
            });
        });
        return Object.keys(levels).filter(key => !childKeys.has(key));
    }, [activeConfig]);

    // Fetch ALL views list on mount
    const fetchViews = useCallback(async () => {
        try {
            const response = await fetch('/api/v3/canvas', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setViews(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error('[DataCanvas] Failed to fetch views:', err);
        }
    }, [token]);

    useEffect(() => {
        fetchViews();
    }, [fetchViews]);

    // Helper to add root grid from sidebar or connection manager — routes through CanvasDispatcher
    const handleAddRootGrid = useCallback((connectionPrefix, schema, table, path) => {
        if (table && (table.includes('FILE_SYSTEM::') || table.includes('{FS}'))) {
            setDraftConfig(prev => CanvasDispatcher.dispatch(
                { type: 'ADD_ROOT_FS', dropZoneId: 'canvas', connectionPrefix, path: table.replace('{FS}', '') },
                prev, defaultConfig
            ));
        } else {
            setDraftConfig(prev => CanvasDispatcher.dispatch(
                { type: 'ADD_ROOT_TABLE', dropZoneId: 'canvas', connectionPrefix, schema, table, path },
                prev, defaultConfig
            ));
        }
    }, [defaultConfig]);

    // ── Add a FILE_ITEM to the canvas — routes through CanvasDispatcher ──────────
    const handleAddFileItem = useCallback((fileData, template) => {
        setDraftConfig(prev => CanvasDispatcher.dispatch(
            { type: 'ADD_FILE', fileData, template },
            prev, defaultConfig
        ));
    }, [defaultConfig]);

    // ── Update a FILE_ITEM size/position ───────────────────────────────────────
    const handleFileItemUpdate = useCallback((instanceId, updates) => {
        setDraftConfig(prev => {
            const current = prev || defaultConfig;
            const fileItems = (current.fileItems || []).map(fi =>
                fi.instanceId === instanceId ? { ...fi, ...updates } : fi
            );
            return { ...current, fileItems };
        });
    }, [defaultConfig]);

    // ── Remove a FILE_ITEM from canvas ─────────────────────────────────────────
    const handleFileItemRemove = useCallback((instanceId) => {
        setDraftConfig(prev => {
            const current = prev || defaultConfig;
            const fileItems = (current.fileItems || []).filter(fi => fi.instanceId !== instanceId);
            return { ...current, fileItems };
        });
    }, [defaultConfig]);

    // ── Handle FILE_ITEM canvas drop (lookup templates, show picker if >1) ─────
    const handleFileItemDrop = useCallback(async (fileData) => {
        try {
            const ext = (fileData.extension || '').toLowerCase();
            const res = await fetch(`/api/v3/file-templates/mappings?extension=${encodeURIComponent(ext)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const mappings = res.ok ? await res.json() : [];

            if (mappings.length === 0) {
                // No template found — use a built-in METADATA_CARD fallback
                handleAddFileItem(fileData, { TemplateType: 'METADATA_CARD', ConfigurationJSON: null });
            } else if (mappings.length === 1) {
                // Only one template — add directly
                handleAddFileItem(fileData, mappings[0]);
            } else {
                // Multiple templates — show picker modal
                setPendingFileDrop({ ...fileData, templates: mappings });
                setSelectedPickerTemplateId(mappings.find(m => m.IsDefault)?.TemplateID || mappings[0].TemplateID);
            }
        } catch (err) {
            console.error('[DataCanvas] Error resolving file template:', err);
            handleAddFileItem(fileData, { TemplateType: 'METADATA_CARD', ConfigurationJSON: null });
        }
    }, [token, handleAddFileItem]);

    const availableTableFilters = useMemo(() => {
        const set = new Set();
        views.forEach(v => {
            if (v.RootPath) {
                let clean = v.RootPath;
                if (clean.includes(']')) clean = clean.split(']').pop();
                if (clean.includes('->')) clean = clean.split('->').pop();
                set.add(clean);
            }
        });
        return Array.from(set);
    }, [views]);

    const filteredViews = useMemo(() => {
        if (selectedTableFilter === 'All') return views;
        return views.filter(v => v.RootPath && v.RootPath.includes(selectedTableFilter));
    }, [views, selectedTableFilter]);

    const handleLoadView = useCallback(async (selectedId) => {
        if (!selectedId) {
            setViewId(null);
            setDraftConfig(null);
            return;
        }
        const numId = parseInt(selectedId, 10);
        setViewId(numId);
        
        // Find view in cached list
        const target = views.find(v => (v.ViewID === numId || String(v.ViewID) === String(selectedId)));
        if (target && target.ConfigurationJSON) {
            try {
                const parsed = typeof target.ConfigurationJSON === 'string' ? JSON.parse(target.ConfigurationJSON) : target.ConfigurationJSON;
                setDraftConfig(parsed);
                return;
            } catch (e) {
                console.error("Error parsing ConfigurationJSON from local views cache:", e);
            }
        }

        // Fetch from backend
        try {
            setLoading(true);
            const res = await fetch(`/api/v3/canvas/${selectedId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const parsed = typeof data.ConfigurationJSON === 'string' ? JSON.parse(data.ConfigurationJSON) : data.ConfigurationJSON;
                setDraftConfig(parsed || defaultConfig);
            }
        } catch (err) {
            console.error("Error loading view configuration:", err);
        } finally {
            setLoading(false);
        }
    }, [views, token, defaultConfig]);

    const handleUpdateCurrentView = async () => {
        if (!viewId) return;
        setIsSavingView(true);
        try {
            const firstRoot = activeConfig.rootGrids && activeConfig.rootGrids[0] ? activeConfig.rootGrids[0].syntaxPath : 'DataCanvas';
            const res = await fetch(`/api/v3/canvas/${viewId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    RootPath: firstRoot,
                    ConfigurationJSON: draftConfig || defaultConfig
                })
            });
            if (!res.ok) throw new Error('Failed to update view');
            await fetchViews();
            alert('Saved Canvas View updated successfully!');
        } catch (err) {
            alert(`Error updating view: ${err.message}`);
        } finally {
            setIsSavingView(false);
        }
    };

    const handleCreateView = async (e) => {
        e.preventDefault();
        if (!newViewName.trim()) return;
        setIsSavingView(true);
        try {
            const firstRoot = activeConfig.rootGrids && activeConfig.rootGrids[0] ? activeConfig.rootGrids[0].syntaxPath : 'DataCanvas';
            const res = await fetch('/api/v3/canvas', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    Name: newViewName,
                    Description: 'Custom multi-grid layout',
                    RootPath: firstRoot,
                    IsDefault: false,
                    ConfigurationJSON: draftConfig
                })
            });
            if (!res.ok) throw new Error('Failed to create view');
            const createdView = await res.json();
            await fetchViews();
            setViewId(createdView.viewId || createdView.ViewID);
            setShowCreateModal(false);
            setNewViewName('');
        } catch (err) {
            alert(`Error creating view: ${err.message}`);
        } finally {
            setIsSavingView(false);
        }
    };

    return (
        <div className="flex h-screen w-full bg-slate-900 text-slate-100 overflow-hidden font-sans">
            {/* Collapsible Universe Explorer Sidebar */}
            <UniverseExplorerSidebar 
                token={token}
                connectionsTree={connectionsTree}
                activeGrid={activeGrid}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(prev => !prev)}
                onSelectTable={(connPrefix, schema, table) => handleAddRootGrid(connPrefix, schema, table)}
            />

            {/* Main Canvas Workspace */}
            <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-950">
                {/* Top Canvas Toolbar */}
                <div className="p-3 bg-slate-900 border-b border-slate-800 flex justify-between items-center z-10 shadow-md">
                    <div className="flex items-center space-x-3">
                        <span className="font-bold text-sm text-sky-400">DataCanvas V3</span>
                        
                        {/* Mode Toggle: View (Data) vs Edit (Structure) */}
                        <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">
                            <button
                                onClick={() => setIsStructureMode(false)}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${!isStructureMode ? 'bg-sky-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                            >
                                📊 Data Mode (View)
                            </button>
                            <button
                                onClick={() => setIsStructureMode(true)}
                                className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${isStructureMode ? 'bg-amber-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                            >
                                🛠️ Structure Mode (Edit)
                            </button>
                        </div>

                        {/* Saved Canvas View Selector Dropdown */}
                        <div className="flex items-center space-x-2 bg-slate-950 px-2.5 py-1 rounded-lg border border-slate-800">
                            <span className="text-xs text-sky-400 font-bold flex items-center space-x-1">
                                <span>🖼️</span>
                                <span>Canvas:</span>
                            </span>
                            <select
                                value={viewId || ''}
                                onChange={(e) => handleLoadView(e.target.value)}
                                className="bg-slate-900 border border-slate-700 text-slate-200 text-xs font-semibold rounded px-2 py-1 focus:outline-none focus:border-sky-500 cursor-pointer max-w-[220px] truncate"
                            >
                                <option value="">-- Scratch Workspace --</option>
                                {filteredViews.map(v => (
                                    <option key={v.ViewID} value={v.ViewID}>
                                        {v.Name} {v.RootPath ? `(${v.RootPath.split('->').pop()})` : ''}
                                    </option>
                                ))}
                            </select>

                            {/* Optional Root Table Filter Dropdown */}
                            {availableTableFilters.length > 0 && (
                                <select
                                    value={selectedTableFilter}
                                    onChange={(e) => setSelectedTableFilter(e.target.value)}
                                    className="bg-slate-900 border border-slate-700 text-slate-400 text-xs rounded px-2 py-1 focus:outline-none focus:border-sky-500 cursor-pointer"
                                    title="Filter saved views by root table"
                                >
                                    <option value="All">All Tables</option>
                                    {availableTableFilters.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        <button 
                            onClick={() => setShowConnectionManager(true)}
                            className="bg-sky-600 hover:bg-sky-500 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors shadow"
                        >
                            + Add Root Grid
                        </button>
                        {viewId && (
                            <button 
                                onClick={handleUpdateCurrentView}
                                disabled={isSavingView}
                                className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded text-xs font-bold transition-colors shadow disabled:opacity-50"
                                title="Update existing saved canvas view"
                            >
                                {isSavingView ? 'Saving...' : '💾 Update Canvas'}
                            </button>
                        )}
                        <button 
                            onClick={() => setShowCreateModal(true)}
                            className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 px-3 py-1.5 rounded text-xs font-bold transition-colors"
                            title="Save current canvas layout as a new named view"
                        >
                            Save As New Canvas
                        </button>
                    </div>
                </div>

                {/* Canvas Workspace Grids Container (Receives Canvas Drag & Drop) */}
                <div 
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={async (e) => {
                        e.preventDefault();
                        try {
                            const rawData = e.dataTransfer.getData('text/plain');
                            if (!rawData) return;
                            const dragItem = JSON.parse(rawData);

                            if (dragItem.type === 'TABLE_NODE') {
                                setDraftConfig(prev => CanvasDispatcher.dispatch(
                                    { type: 'ADD_ROOT_TABLE', dropZoneId: 'canvas', connectionPrefix: dragItem.connectionPrefix || '', schema: dragItem.schema || 'dbo', table: dragItem.table || '', path: dragItem.path || '' },
                                    prev, defaultConfig
                                ));
                            } else if (dragItem.type === 'DIRECTORY_NODE') {
                                setDraftConfig(prev => CanvasDispatcher.dispatch(
                                    { type: 'ADD_ROOT_FS', dropZoneId: 'canvas', connectionPrefix: dragItem.connectionPrefix || '', path: dragItem.path || 'C:\\' },
                                    prev, defaultConfig
                                ));
                            } else if (dragItem.type === 'COLUMN_NODE') {
                                setDraftConfig(prev => CanvasDispatcher.dispatch(
                                    { type: 'ADD_COLUMN', dropZoneId: 'canvas', connectionPrefix: dragItem.connectionPrefix || '', schema: dragItem.schema || 'dbo', table: dragItem.table || '', columnKey: dragItem.columnKey || '', path: dragItem.path || '' },
                                    prev, defaultConfig
                                ));
                            } else if (dragItem.type === 'FILE_ITEM') {
                                await handleFileItemDrop({
                                    filePath:        dragItem.filePath,
                                    fileName:        dragItem.fileName,
                                    extension:       dragItem.extension,
                                    fileSize:        dragItem.fileSize,
                                    fileDateModified: dragItem.fileDateModified,
                                    connectionPrefix: dragItem.connectionPrefix || ''
                                });
                            }
                        } catch (err) {
                            console.error('Error handling canvas workspace drop:', err);
                        }
                    }}
                    className="flex-1 overflow-auto p-6 space-y-6 datacanvas-canvas-area"
                >
                    {/* Canvas File Items — rendered above grids in a flex-wrap row */}
                    {(activeConfig.fileItems || []).length > 0 && (
                        <div className="flex flex-wrap gap-4 mb-4">
                            {(activeConfig.fileItems || []).map(fi => (
                                <CanvasFileItem
                                    key={fi.instanceId}
                                    instanceId={fi.instanceId}
                                    filePath={fi.filePath}
                                    fileName={fi.fileName}
                                    extension={fi.extension}
                                    fileSize={fi.fileSize}
                                    fileDateModified={fi.fileDateModified}
                                    connectionPrefix={fi.connectionPrefix}
                                    templateType={fi.templateType}
                                    templateConfig={fi.templateConfig}
                                    position={fi.position}
                                    size={fi.size}
                                    token={token}
                                    isStructureMode={isStructureMode}
                                    onUpdate={handleFileItemUpdate}
                                    onRemove={handleFileItemRemove}
                                />
                            ))}
                        </div>
                    )}

                    {rootGridKeys && rootGridKeys.length > 0 ? (
                        rootGridKeys.map((instanceId) => {
                            const grid = activeConfig.levels?.[instanceId];
                            if (!grid || !grid.syntaxPath) return null;
                            return (
                                <div 
                                    key={instanceId}
                                    onClick={() => setActiveGrid({ instanceId, path: grid.syntaxPath })}
                                    className={`transition-all rounded-lg ${activeGrid?.instanceId === instanceId ? 'ring-2 ring-sky-500 shadow-xl' : ''}`}
                                >
                                    <CanvasGrid 
                                        syntaxPath={grid.syntaxPath}
                                        instanceId={instanceId}
                                        parentRowId={null}
                                        canvasConfig={activeConfig}
                                        setCanvasConfig={setDraftConfig}
                                        globalParameters={globalParameters}
                                        token={token}
                                        isStructureMode={isStructureMode}
                                        targetSchemaGroup={grid.syntaxPath.includes(']') ? grid.syntaxPath.split(']')[0].replace('[', '') : 'Default'}
                                        onRemoveGrid={(removedId) => {
                                            setDraftConfig(prev => CanvasDispatcher.dispatch({ type: 'REMOVE_GRID', instanceId: removedId }, prev));
                                        }}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center text-slate-500 space-y-4 py-20 border-2 border-dashed border-slate-800 rounded-xl hover:border-sky-500/50 transition-colors">
                            <div className="w-20 h-20 text-slate-700">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s-8-1.79-8-4" />
                                </svg>
                            </div>
                            <p className="text-lg font-medium text-slate-400">Your Data Canvas is empty.</p>
                            <p className="text-xs text-slate-500">Drag &amp; drop tables or columns from Universe Explorer onto this canvas, or click "+ Add Root Grid" to start.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Template Picker Modal (shown when multiple templates exist for a file extension) ─ */}
            {pendingFileDrop && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[300]">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-96 p-6 space-y-4">
                        <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                            <div>
                                <h3 className="font-bold text-slate-100 text-sm">Choose Display Template</h3>
                                <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[300px]" title={pendingFileDrop.filePath}>
                                    {pendingFileDrop.fileName}
                                </p>
                            </div>
                            <button onClick={() => setPendingFileDrop(null)} className="text-slate-500 hover:text-slate-200 font-bold text-lg leading-none">✕</button>
                        </div>

                        <div className="space-y-2">
                            {pendingFileDrop.templates.map(tpl => (
                                <label
                                    key={tpl.TemplateID}
                                    className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                        selectedPickerTemplateId === tpl.TemplateID
                                            ? 'border-sky-500 bg-sky-950/50'
                                            : 'border-slate-700 hover:border-slate-500 bg-slate-800/50'
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="fileTemplate"
                                        value={tpl.TemplateID}
                                        checked={selectedPickerTemplateId === tpl.TemplateID}
                                        onChange={() => setSelectedPickerTemplateId(tpl.TemplateID)}
                                        className="mt-1 accent-sky-500"
                                    />
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">
                                            {tpl.TemplateType === 'IMAGE_VIEWER' ? '🖼 ' : '📋 '}
                                            {tpl.DisplayName || tpl.TemplateName}
                                        </div>
                                        {tpl.Description && (
                                            <div className="text-xs text-slate-400 mt-0.5">{tpl.Description}</div>
                                        )}
                                        {tpl.IsDefault && (
                                            <span className="text-[10px] text-sky-400 font-semibold">★ Default</span>
                                        )}
                                    </div>
                                </label>
                            ))}
                        </div>

                        <div className="flex justify-end space-x-3 pt-2">
                            <button
                                onClick={() => setPendingFileDrop(null)}
                                className="px-4 py-2 border border-slate-600 rounded text-sm text-slate-300 hover:bg-slate-800 font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const selected = pendingFileDrop.templates.find(t => t.TemplateID === selectedPickerTemplateId);
                                    if (selected) handleAddFileItem(pendingFileDrop, selected);
                                    setPendingFileDrop(null);
                                }}
                                className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded text-sm font-bold shadow"
                            >
                                Add to Canvas
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create View Modal Overlay */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[200]">
                    <form onSubmit={handleCreateView} className="bg-white p-6 rounded-lg shadow-2xl border border-gray-200 w-96 text-gray-900">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Save Custom Grid View</h3>
                        <div className="mb-4">
                            <label className="block text-sm font-bold text-gray-700 mb-1">View Name</label>
                            <input 
                                autoFocus
                                type="text"
                                value={newViewName}
                                onChange={(e) => setNewViewName(e.target.value)}
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                                placeholder="e.g. Operations Dashboard"
                                required
                            />
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button 
                                type="button" 
                                onClick={() => { setShowCreateModal(false); setNewViewName(''); }}
                                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-100 font-medium"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                disabled={isSavingView}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-sm font-bold shadow-sm"
                            >
                                {isSavingView ? 'Saving...' : 'Create View'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Connection Manager Modal Overlay */}
            {showConnectionManager && (
                <ConnectionManager 
                    onClose={() => setShowConnectionManager(false)}
                    token={token}
                    onAddConnection={({ connectionString, rootTable }) => {
                        setShowConnectionManager(false);
                        const parts = rootTable.split('.');
                        const schema = parts.length > 1 ? parts[0] : 'dbo';
                        const table = parts.length > 1 ? parts[1] : parts[0];
                        handleAddRootGrid(connectionString, schema, table);
                    }}
                />
            )}
        </div>
    );
};

export default DataCanvas;
